/* @flow */
import type { Node } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	AdvertButton,
	AdvertButtonId,
	AdvertButtonsWrapper,
	AdvertButtonText,
	AdvertDescription,
	AdvertInfo,
	AdvertLink,
	AdvertPrice,
	AdvertRoot,
	AdvertTabButton,
	AdvertTabWrapper,
	DropdownIcon,
	Title,
	ToggleButton,
} from './styles';
import { formatValuesNumber } from '../../../../utils/formatting';
import { dropdownIcon } from '../../../Icons';
import type { TRootState } from '../../../../types/rootState';
import { TApartAdverts } from '../../../../types/apart';
import ImagesSliderSmall from '../../../ImagesSliderSmall';

const Advert = ({ apartAdvert }: TApartAdverts): Node => {
	const isMobile: boolean = useSelector((state: TRootState) => state.app.isMobile);

	const [selectedAd, setSelectedAd] = useState(apartAdvert[0]);
	const [startAd, setStartAd] = useState(0);
	const [activeBtn, setActiveBtn] = useState(0);
	const [isOpen, setIsOpen] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const descriptionRef = useRef(null);

	useEffect(() => {
		const element = descriptionRef.current;
		if (element?.offsetHeight < element?.scrollHeight || element?.offsetWidth < element?.scrollWidth) {
			setHasOverflow(true);
		}
	}, [descriptionRef]);

	const onAdvertClick = (id) => {
		setIsOpen(false);
		setSelectedAd(apartAdvert[id]);
		setActiveBtn(id);
		setStartAd(id);
	};

	const onTabClick = (forward) => {
		const next = forward ? startAd + 1 : startAd - 1;
		setStartAd(next);

		if (isMobile) {
			setIsOpen(false);
			setSelectedAd(apartAdvert[next]);
			setActiveBtn(next);
		}
	};

	const toggleOpen = () => {
		setIsOpen(!isOpen);
	};

	const getShortId = (id) => {
		if (id.length > 5) {
			return `...${id.slice(-4)}`;
		}
		return id;
	};

	return (
		<AdvertRoot>
			<Title>Объявления</Title>
			<AdvertButtonsWrapper $withTabs={apartAdvert?.length > 4}>
				{apartAdvert?.length > 4 && (
					<AdvertTabWrapper>
						<AdvertTabButton $isPrev disabled={startAd === 0} onClick={() => onTabClick(false)}>
							Назад
						</AdvertTabButton>
						<AdvertTabButton
							$isNext
							disabled={isMobile ? startAd === apartAdvert - 1 : startAd === apartAdvert.length - 3}
							onClick={() => onTabClick(true)}
						>
							Вперёд
						</AdvertTabButton>
					</AdvertTabWrapper>
				)}
				{apartAdvert?.map((advert, idx: number) => (
					<AdvertInfo
						key={Math.random()}
						onClick={() => onAdvertClick(idx)}
						$isActive={idx === activeBtn}
						$isHidden={
							isMobile ? idx !== startAd : apartAdvert.length > 4 && (idx < startAd || idx > startAd + 3)
						}
					>
						<AdvertButton $isActive={idx === activeBtn} idx={idx} length={apartAdvert.length}>
							<AdvertButtonText $isActive={idx === activeBtn}>
								{advert.source === 'AVITO' ? 'Авито' : 'Циан'}
							</AdvertButtonText>
							<AdvertButtonId $isActive={idx === activeBtn}>
								{apartAdvert.length > 2 || isMobile
									? `# ${getShortId(advert.sourceAdvertId.toString())}`
									: `# ${advert.sourceAdvertId}`}
							</AdvertButtonId>
						</AdvertButton>
						<AdvertPrice $isActive={idx === activeBtn}>
							{formatValuesNumber(advert.price, 'rub')}
						</AdvertPrice>
					</AdvertInfo>
				))}
			</AdvertButtonsWrapper>
			<AdvertDescription ref={descriptionRef} isOpen={isOpen}>
				{selectedAd?.description}
			</AdvertDescription>
			{hasOverflow && (
				<ToggleButton onClick={toggleOpen} type="button">
					{isOpen ? 'Свернуть' : 'Развернуть'}

					<DropdownIcon viewBox="0 0 10 5" isOpen={isOpen}>
						{dropdownIcon}
					</DropdownIcon>
				</ToggleButton>
			)}
			{selectedAd?.images.length > 0 && (
				<ImagesSliderSmall
					images={selectedAd?.images}
					apartId={selectedAd?.sourceAdvertId}
					isMobile={isMobile}
					withoutScroll
				/>
			)}
			{selectedAd?.sourceUrl && (
				<AdvertLink to={`/external-link/?url=${selectedAd?.sourceUrl}`} target="_blank">
					Перейти к объявлению&nbsp;&#8594;
				</AdvertLink>
			)}
		</AdvertRoot>
	);
};

export default Advert;
